import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Google Analytics 4 helpers.
 *
 * gtag.js is bootstrapped in /public/index.html with send_page_view=false,
 * so the SPA sends page_view events manually on every react-router navigation.
 * Every helper here is a no-op if window.gtag isn't defined (e.g. dev/preview
 * with an ad-blocker or when the tag hasn't loaded yet), so it can never break
 * the app.
 */

const isEnabled = () => typeof window !== "undefined" && typeof window.gtag === "function";

/** Fire a GA4 page_view for the current path (with optional custom title). */
export const trackPageview = (path, title) => {
  if (!isEnabled()) return;
  try {
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: title || document.title,
    });
  } catch (_e) {
    /* swallow */
  }
};

/** Fire an arbitrary GA4 event with params. */
export const trackEvent = (name, params = {}) => {
  if (!isEnabled()) return;
  try {
    window.gtag("event", name, params);
  } catch (_e) {
    /* swallow */
  }
};

/**
 * React hook: emits a GA4 page_view on every location change (SPA navigation).
 * Mount once inside <BrowserRouter>.
 */
export const useTrackPageViews = () => {
  const location = useLocation();
  useEffect(() => {
    const path = `${location.pathname}${location.search || ""}`;
    // Slight delay so document.title reflects the new page (react-router-driven titles)
    const t = setTimeout(() => trackPageview(path), 60);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);
};
